/**
 * Canvas Fabric.js que renderiza uma imagem com bounding boxes vindas do
 * backend (E4.F1) e, opcionalmente, permite ao perito mover/redimensionar
 * cada BB sem persistir (E4.F2a — edição efêmera).
 *
 * Modos:
 * - `editable=false` (default, E4.F1): retângulos read-only com
 *   `selectable=false`, `evented=false`. Caminho usado pelo canvas da
 *   referência e por qualquer consumidor que só precise visualizar.
 * - `editable=true` (E4.F2a): retângulos `selectable=true`, controles de
 *   resize visíveis (sem handle de rotação — preserva alinhamento forense).
 *   Cada `object:modified` emite o array completo de BBs (coords convertidas
 *   de volta da escala do canvas para a escala da imagem original) via
 *   `onBoundingBoxesChange`.
 *
 * Importante: o canvas é a fonte da verdade enquanto está montado. A prop
 * `boundingBoxes` semeia o estado inicial; mudanças subsequentes vindas do
 * pai NÃO re-montam o canvas (evita loop infinito React ↔ Fabric). O
 * `data-bbox-count` no wrapper reflete a prop atual (espelho do estado
 * controlado pelo pai), facilitando asserts em Vitest e Playwright.
 */
import { useEffect, useRef } from 'react';
import * as fabric from 'fabric';
import { ptBR } from '@/i18n/pt-BR';
import type { CanvasBoundingBoxData } from '@/lib/analyses';
import { SIGNAL_HEX, TOKEN_HEX } from '@/lib/design_tokens';

interface AnalysisCanvasProps {
  imageUrl: string | null;
  boundingBoxes: CanvasBoundingBoxData[];
  altText: string;
  testId: string;
  maxWidthPx?: number;
  maxHeightPx?: number;
  editable?: boolean;
  onBoundingBoxesChange?: ((next: CanvasBoundingBoxData[]) => void) | undefined;
}

const DEFAULT_MAX_WIDTH = 520;
const DEFAULT_MAX_HEIGHT = 320;
/**
 * E54.F8.T08: a região de evidência deixou de ser o vermelho saturado da V0 e
 * passou à rampa de sinal do pacote — é a mesma fresta do canvas Fabric da
 * revisão de extração (F7.T10): o parser de cor da lib não resolve `var()`, e
 * era por aí que a paleta antiga sobrevivia inteira à troca de paleta da F1.
 */
const BB_STROKE = SIGNAL_HEX.neg;
const BB_STROKE_WIDTH = 2;
const FILL_ALPHA = 0.1;

function veu(hex: string): string {
  const limpo = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(limpo.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${FILL_ALPHA})`;
}

const BB_FILL_ALPHA = veu(SIGNAL_HEX.neg);

export function AnalysisCanvas({
  imageUrl,
  boundingBoxes,
  altText,
  testId,
  maxWidthPx = DEFAULT_MAX_WIDTH,
  maxHeightPx = DEFAULT_MAX_HEIGHT,
  editable = false,
  onBoundingBoxesChange,
}: AnalysisCanvasProps) {
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);

  // Refs lidas pelo effect a cada mount para evitar re-mount em cada edição:
  // o canvas Fabric é a fonte da verdade quando `editable=true`; o React
  // state apenas espelha (props.boundingBoxes alimenta `data-bbox-count`).
  const onChangeRef = useRef(onBoundingBoxesChange);
  onChangeRef.current = onBoundingBoxesChange;
  const initialBboxesRef = useRef(boundingBoxes);
  initialBboxesRef.current = boundingBoxes;

  useEffect(() => {
    if (canvasElRef.current === null || imageUrl === null) return undefined;
    const canvasEl = canvasElRef.current;
    const fabricCanvas = new fabric.Canvas(canvasEl, {
      selection: editable,
      renderOnAddRemove: true,
      backgroundColor: TOKEN_HEX.paper,
    });
    fabricRef.current = fabricCanvas;

    // Mantém pares (BB original, fabric.Rect ao vivo) na ordem de inserção
    // para que o emit reconstrua o array preservando `criterion_id`/`label`.
    const rects: { bb: CanvasBoundingBoxData; rect: fabric.Rect }[] = [];

    let cancelled = false;
    let scale = 1;
    void (async () => {
      try {
        const img = await fabric.FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' });
        if (cancelled || fabricRef.current === null) return;
        const naturalWidth = img.width ?? 1;
        const naturalHeight = img.height ?? 1;
        scale = Math.min(maxWidthPx / naturalWidth, maxHeightPx / naturalHeight, 1);
        const displayWidth = naturalWidth * scale;
        const displayHeight = naturalHeight * scale;
        fabricCanvas.setDimensions({ width: displayWidth, height: displayHeight });
        img.set({
          left: 0,
          top: 0,
          scaleX: scale,
          scaleY: scale,
          selectable: false,
          evented: false,
        });
        fabricCanvas.add(img);
        fabricCanvas.sendObjectToBack(img);

        for (const bb of initialBboxesRef.current) {
          const rect = new fabric.Rect({
            left: bb.x * scale,
            top: bb.y * scale,
            width: bb.width * scale,
            height: bb.height * scale,
            fill: BB_FILL_ALPHA,
            stroke: BB_STROKE,
            strokeWidth: BB_STROKE_WIDTH,
            selectable: editable,
            evented: editable,
            hoverCursor: editable ? 'move' : 'default',
            lockMovementX: !editable,
            lockMovementY: !editable,
            lockScalingFlip: true,
          });
          if (editable && typeof rect.setControlsVisibility === 'function') {
            // Desabilita o handle de rotação (`mtr`): preserva alinhamento
            // axial da evidência forense. Resize livre em todas as bordas.
            rect.setControlsVisibility({ mtr: false });
          }
          rects.push({ bb, rect });
          fabricCanvas.add(rect);
        }
        fabricCanvas.requestRenderAll();
      } catch {
        // Falha silenciosa — fallback do componente é o <span> abaixo com altText.
      }
    })();

    function emitChange() {
      const onChange = onChangeRef.current;
      if (onChange === undefined) return;
      const next = rects.map(({ bb, rect }) => {
        const left = rect.left ?? 0;
        const top = rect.top ?? 0;
        const width = (rect.width ?? 0) * (rect.scaleX ?? 1);
        const height = (rect.height ?? 0) * (rect.scaleY ?? 1);
        return {
          ...bb,
          x: left / scale,
          y: top / scale,
          width: width / scale,
          height: height / scale,
        };
      });
      onChange(next);
    }

    if (editable) {
      fabricCanvas.on('object:modified', emitChange);
    }

    return () => {
      cancelled = true;
      fabricRef.current = null;
      if (editable) fabricCanvas.off('object:modified', emitChange);
      fabricCanvas.dispose();
    };
  }, [imageUrl, maxWidthPx, maxHeightPx, editable]);

  return (
    <div
      data-testid={testId}
      data-bbox-count={boundingBoxes.length}
      data-editable={editable ? 'true' : 'false'}
      role="img"
      aria-label={altText}
      className="relative inline-block max-w-full overflow-hidden border border-border bg-paper"
    >
      <canvas ref={canvasElRef} className="block max-w-full" />
      {imageUrl === null ? (
        <span className="block px-sc-4 py-sc-6 text-body-sm text-fg-muted">
          {ptBR.analise.imagensCarregando}
        </span>
      ) : null}
    </div>
  );
}
